import { Heading } from "monday-ui-react-core/next";
import {
  Table,
  TableBody,
  TableHeader,
  TableHeaderCell,
} from "monday-ui-react-core";
import { useCallback, useEffect, useMemo } from "react";
import { useStore } from "../../store";
import Container from "../BuildingBlocks/Container/Container";
import YearlyInvestmentSummaryDisplay from "./YearlyInvestmentSummaryDisplay/YearlyInvestmentSummaryDisplay";
import Classes from "./InvestmentSummaryDisplay.module.scss";
import { toJS } from "mobx";
import { observer } from "mobx-react";
import ProjectionSummaryDisplay from "./ProjectionSumarryDisplay/ProjectionSummaryDisplay";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";

const InvestmentSummaryDisplay = () => {
  const { context } = useStore();
  const { investmentSummaryDisplayRef } = context;

  // console.log("Chart data:", toJS(context.investmentSummary));


  const tableColumns = useMemo(
    () => [
      {
        id: "year",
        title: "שנה",
        infoContent: "",
      },
      {
        id: "yearly-deposit",
        title: "הפקדה\nשנתית",
        infoContent: "",
      },
      {
        id: "yearly-earnings",
        title: "רווח שנתי",
        infoContent: "",
      },
      {
        id: "total-earnings",
        title: "סה״כ חיסכון",
        infoContent: "",
      },
    ],
    []
  );

  useEffect(() => {
    context.investmentSummaryDisplayRef.current?.scrollIntoView({
      behavior: "smooth",
    });
  }, [context.investmentSummary, context.investmentSummaryDisplayRef]);

  const renderTableHeaders = useCallback(() => {
    return Object.values(tableColumns).map((column) => (
      <TableHeaderCell
        title={column.title}
        key={column.id}
        className={Classes.tableHeader}
      />
    ));
  }, [tableColumns]);

  return (
    <div ref={investmentSummaryDisplayRef}>
      {toJS(context.investmentSummary).length === 0 ? null : (
        <>
          <Container className={Classes.projectionWrapper}>
            <div className={Classes.projectionContainer}>
              <Heading
                type={Heading.types.H2}
                weight={Heading.weights.NORMAL}
                className={Classes.header}
              >
                תחזית ל-{context.numberOfYears} שנים
              </Heading>
              <ProjectionSummaryDisplay />
            </div>
          </Container>
          <Container className={Classes.projectionWrapper}>
            <Heading
              type={Heading.types.H3}
              weight={Heading.weights.NORMAL}
              className={Classes.chartHeader}
            >
              התפתחות החיסכון לאורך השנים
            </Heading>

            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={toJS(context.investmentSummary)}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="year" />
                <YAxis tickFormatter={(v) => v.toLocaleString()} />
                <Tooltip
                  formatter={(value: number) =>
                    `₪${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
                  }
                />
                <Line
                  type="monotone"
                  dataKey="totalEarnings"
                  name="סה״כ חיסכון"
                  stroke="#f2cb05"
                  strokeWidth={3}
                  dot={{ r: 5 }}
                />
                <Line
                  type="monotone"
                  name="רווח שנתי"
                  dataKey="yearlyEarnings"
                  stroke="#0f8c2f"
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </Container>
          <Container className={Classes.tableContainer}>
            <Table
              columns={tableColumns}
              emptyState={<div />}
              errorState={<div />}
              className={Classes.table}
            >
              <TableHeader>{renderTableHeaders()}</TableHeader>
              {/* Changes made here to support chart data structure */}
              <TableBody>
                {context.investmentSummary.map((yearlyInvestmentSummary) => {
                  // Extract only the first 4 properties
                  const { year, deposits, earnings, balance } = yearlyInvestmentSummary;

                  return (
                    <YearlyInvestmentSummaryDisplay
                      key={year}
                      yearlyInvestmentSummary={{ year, deposits, earnings, balance }}
                    />
                  );
                })}
              </TableBody>
            </Table>
          </Container>
        </>
      )}
    </div>
  );
};

export default observer(InvestmentSummaryDisplay);
